import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import ReusableCard from "../../utils/reusable_card";
import ConfirmationModal from "../../utils/general/confirmationModal";
import { showToast } from "../../components/ToastNotification";

const API_URL = "https://server-postgre.vercel.app/api/teams/my";

const MyTeams = () => {
  const token = useSelector((state) => state.user.token);

  const [userId, setUserId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [search, setSearch] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deleteId, setDeleteId] = useState(null);

  const hasMore = page < pages;

  // Skeleton card
  const SkeletonCard = () => (
    <div className="animate-pulse rounded-2xl border border-neutral-200 p-3 h-52 bg-neutral-100" />
  );

  // Leer userId desde token
  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setUserId(decoded.id);
    }
  }, [token]);

  // Fetch teams
  const fetchTeams = useCallback(
    async (targetPage = 1, replace = false) => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);

        const resp = await fetch(
          `${API_URL}?page=${targetPage}&search=${debounceSearch}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!resp.ok) throw new Error("Error al cargar equipos");

        const json = await resp.json();

        const mapped = json.data.map((team) => ({
          id: team.id,
          name: team.team_name,
          photo: team.picture_url,
          city: team.city || "Sin ciudad",
          createdAt: team.created_at,
        }));

        setTeams((prev) => (replace ? mapped : [...prev, ...mapped]));
        setPages(json.pages);
        setPage(json.page);
      } catch (e) {
        setError("Error al cargar los equipos.");
        showToast.error(e.message);
      } finally {
        setLoading(false);
      }
    },
    [userId, token, debounceSearch],
  );

  // Primera carga + cuando cambia búsqueda
  useEffect(() => {
    fetchTeams(1, true);
  }, [fetchTeams]);

  // Debounce búsqueda (300ms)
  useEffect(() => {
    const handler = setTimeout(() => setDebounceSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const resp = await fetch(
        `https://server-postgre.vercel.app/api/teams/${deleteId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) throw new Error("Error al eliminar el equipo");

      setTeams((prev) => prev.filter((t) => t.id !== deleteId));
      showToast.success("Equipo eliminado");
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setDeleteId(null);
    }
  };

  const handleLoadMore = () => hasMore && fetchTeams(page + 1);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-center gap-3 justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Mis Equipos</h1>
        <input
          type="text"
          placeholder="Buscar equipo..."
          className="border rounded-lg px-3 py-2 w-52 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </header>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 mb-3 rounded-md border border-red-200">
          {error}
        </div>
      )}

      {!teams.length && !loading && (
        <p className="text-neutral-600 text-sm">No tienes equipos.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
        {teams.map((team) => (
          <div key={team.id} className="relative">
            <a href={`/teams/${team.id}`}>
              <ReusableCard
                image={team.photo}
                name={team.name}
                city={team.city}
                date={team.createdAt}
              />
            </a>

            {/* Icono de eliminar */}
            <button
              className="absolute top-2 right-2 text-xs bg-red-500 text-white px-2 py-[2px] rounded"
              onClick={(e) => {
                e.preventDefault();
                setDeleteId(team.id);
              }}
            >
              Eliminar
            </button>
          </div>
        ))}

        {loading && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}
      </div>

      {hasMore && !loading && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            className="px-5 py-2.5 rounded-lg bg-black text-white hover:bg-neutral-800 transition text-sm"
          >
            Cargar más
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message="¿Eliminar equipo?"
      />
    </div>
  );
};

export default MyTeams;
